/**
 * Forgot Password Form Component
 * Material-UI based password reset request form
 * Requirements: 6.7
 */
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Box,
  Button,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  Paper,
  Link as MuiLink,
} from '@mui/material';
import { Link } from 'react-router-dom';
import { resetPasswordRequestSchema, type ResetPasswordRequestFormData } from '../schemas/auth.schema';
import { useAuth } from '../context/AuthContext';

export const ForgotPasswordForm: React.FC = () => {
  const { resetPassword, isLoading, error, clearError } = useAuth();
  const [isSubmitted, setIsSubmitted] = useState(false);
  // 確認コード画面へ引き継ぐため、送信したメールアドレスを保持する
  const [submittedEmail, setSubmittedEmail] = useState('');

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ResetPasswordRequestFormData>({
    resolver: zodResolver(resetPasswordRequestSchema),
    defaultValues: {
      email: '',
    },
  });

  const onSubmit = async (data: ResetPasswordRequestFormData) => {
    try {
      clearError();
      await resetPassword(data.email);
      setSubmittedEmail(data.email);
      setIsSubmitted(true);
    } catch {
      // Error is handled by AuthContext
    }
  };

  if (isSubmitted) {
    return (
      <Paper
        elevation={3}
        sx={{
          p: 4,
          maxWidth: 400,
          width: '100%',
          mx: 'auto',
        }}
      >
        <Typography variant="h5" component="h1" gutterBottom align="center">
          メール送信完了
        </Typography>
        <Alert severity="success" sx={{ mb: 2 }}>
          確認コードを送信しました。メールをご確認ください。
        </Alert>
        <Box sx={{ textAlign: 'center', mt: 2 }}>
          <MuiLink component={Link} to="/reset-password" state={{ email: submittedEmail }} variant="body2">
            確認コードを入力する
          </MuiLink>
        </Box>
        <Box sx={{ textAlign: 'center', mt: 2 }}>
          <MuiLink component={Link} to="/login" variant="body2">
            ログインに戻る
          </MuiLink>
        </Box>
      </Paper>
    );
  }

  return (
    <Paper
      elevation={3}
      sx={{
        p: 4,
        maxWidth: 400,
        width: '100%',
        mx: 'auto',
      }}
    >
      <Typography variant="h5" component="h1" gutterBottom align="center">
        パスワードリセット
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3, textAlign: 'center' }}>
        登録されているメールアドレスを入力してください。パスワードリセット用の確認コードをお送りします。
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={clearError}>
          {error}
        </Alert>
      )}

      <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
        <TextField
          {...register('email')}
          label="メールアドレス"
          type="email"
          fullWidth
          margin="normal"
          error={!!errors.email}
          helperText={errors.email?.message}
          autoComplete="email"
          autoFocus
          disabled={isLoading}
        />

        <Button
          type="submit"
          fullWidth
          variant="contained"
          sx={{ mt: 3, mb: 2 }}
          disabled={isLoading}
        >
          {isLoading ? <CircularProgress size={24} /> : '送信'}
        </Button>

        <Box sx={{ textAlign: 'center', mt: 2 }}>
          <MuiLink component={Link} to="/login" variant="body2">
            ログインに戻る
          </MuiLink>
        </Box>
      </Box>
    </Paper>
  );
};
